import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { ChevronDown } from 'lucide-react'
import { Button } from '../ui/button'
import { FieldLabel } from '../ui/field-label'
import { activeCommentTag, splitCommentsByTag } from '../../lib/case-phase'
import { cn } from '../../lib/utils'
import type { Case, CaseComment, CommentChannel } from '../../types/case'

const COMMENT_TAG_LABELS = {
  pre_op: 'Pre-op',
  post_op: 'Post-op',
  national: 'National',
} as const

type CaseCommentsPanelProps = {
  caseItem: Case
  comments: CaseComment[]
  /** review = under-review feedback; national = National Hub discussion */
  channel?: CommentChannel
  /** When set, only show comments from this user (Reviewers & Feedback drill-in). */
  filterUserId?: string | null
  /** Case consultant (`belongs_to_doctor_id`) — always a doctor; may comment while under review. */
  isConsultant: boolean
  canStore: boolean
  canUpdate: boolean
  canDelete: boolean
  isUnderReview: boolean
  /** National channel: case is public and allow_national_comments is on. */
  nationalCommentsEnabled?: boolean
  actionLoading: boolean
  commentBody: string
  onCommentBodyChange: (value: string) => void
  postAnonymously: boolean
  onPostAnonymouslyChange: (value: boolean) => void
  onAddComment: () => void
  editingCommentId: string | null
  editingCommentBody: string
  onEditingCommentBodyChange: (value: string) => void
  onStartEdit: (comment: CaseComment) => void
  onCancelEdit: () => void
  onSaveEdit: (commentId: string) => void
  onDelete: (commentId: string) => void
}

function displayAuthor(comment: CaseComment, channel: CommentChannel): string {
  if (comment.is_anonymous) {
    if (comment.is_own) {
      return `${comment.user?.name ?? 'You'} (anonymous to others)`
    }

    return channel === 'national' ? 'Anonymous doctor' : 'Anonymous reviewer'
  }

  return comment.user?.name ?? comment.user_id ?? 'Unknown'
}

function CommentCard({
  comment,
  channel,
  editingCommentId,
  editingCommentBody,
  onEditingCommentBodyChange,
  onStartEdit,
  onCancelEdit,
  onSaveEdit,
  onDelete,
  canUpdate,
  canDelete,
  actionLoading,
  readOnly = false,
}: {
  comment: CaseComment
  channel: CommentChannel
  editingCommentId: string | null
  editingCommentBody: string
  onEditingCommentBodyChange: (value: string) => void
  onStartEdit: (comment: CaseComment) => void
  onCancelEdit: () => void
  onSaveEdit: (commentId: string) => void
  onDelete: (commentId: string) => void
  canUpdate: boolean
  canDelete: boolean
  actionLoading: boolean
  readOnly?: boolean
}) {
  const isOwn = Boolean(comment.is_own)
  const isEditing = editingCommentId === comment.id

  return (
    <li className="rounded-md border border-parch-100 bg-parch-50 px-3 py-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <p className="text-sm font-medium text-ink-800">
            {displayAuthor(comment, channel)}
          </p>
          {comment.is_anonymous && !comment.is_own && (
            <span className="rounded-full bg-parch-200 px-2 py-0.5 text-xs font-medium text-ink-600">
              Anonymous
            </span>
          )}
          {comment.tag && comment.tag !== 'national' && (
            <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
              {COMMENT_TAG_LABELS[comment.tag]}
            </span>
          )}
        </div>
        <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
          <p className="text-xs text-ink-500">
            {comment.created_at
              ? new Date(comment.created_at).toLocaleString(undefined, {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: true,
                })
              : ''}
          </p>
          {isOwn && !isEditing && !readOnly && (
            <>
              {canUpdate && (
                <Button size="sm" variant="secondary" onClick={() => onStartEdit(comment)}>
                  Edit
                </Button>
              )}
              {canDelete && (
                <Button
                  size="sm"
                  variant="destructive"
                  disabled={actionLoading}
                  onClick={() => onDelete(comment.id)}
                >
                  Delete
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      {isEditing ? (
        <div className="mt-2 space-y-2">
          <textarea
            className="min-h-20 w-full rounded-md border border-parch-200 px-3 py-2 text-sm"
            value={editingCommentBody}
            onChange={(event) => onEditingCommentBodyChange(event.target.value)}
          />
          <div className="flex gap-2">
            <Button size="sm" onClick={() => onSaveEdit(comment.id)} disabled={actionLoading}>
              Save
            </Button>
            <Button size="sm" variant="secondary" onClick={onCancelEdit}>
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <p className="mt-2 whitespace-pre-wrap text-sm text-ink-700">{comment.body}</p>
      )}
    </li>
  )
}

function CollapsibleSection({
  title,
  comments,
  defaultOpen,
  children,
}: {
  title: string
  comments: CaseComment[]
  defaultOpen: boolean
  children: ReactNode
}) {
  const [open, setOpen] = useState(defaultOpen)

  useEffect(() => {
    if (defaultOpen) {
      setOpen(true)
    }
  }, [defaultOpen])

  return (
    <section className="space-y-3">
      <button
        type="button"
        className="flex w-full items-center justify-between gap-2 text-left"
        onClick={() => setOpen((current) => !current)}
      >
        <h3 className="font-display text-base font-semibold text-ink-900">{title}</h3>
        <ChevronDown className={cn('h-4 w-4 text-ink-500 transition-transform', open && 'rotate-180')} />
      </button>
      {open && (
        <ul className="space-y-3">
          {comments.length === 0 ? (
            <li className="text-sm text-ink-500">No comments yet.</li>
          ) : (
            children
          )}
        </ul>
      )}
    </section>
  )
}

/** Review feedback or National Hub comments panels. */
export function CaseCommentsPanel({
  caseItem,
  comments,
  channel = 'review',
  filterUserId = null,
  isConsultant,
  canStore,
  canUpdate,
  canDelete,
  isUnderReview,
  nationalCommentsEnabled = false,
  actionLoading,
  commentBody,
  onCommentBodyChange,
  postAnonymously,
  onPostAnonymouslyChange,
  onAddComment,
  editingCommentId,
  editingCommentBody,
  onEditingCommentBodyChange,
  onStartEdit,
  onCancelEdit,
  onSaveEdit,
  onDelete,
}: CaseCommentsPanelProps) {
  const scopedComments = useMemo(() => {
    if (!filterUserId) {
      return comments
    }

    return comments.filter((comment) => comment.user_id === filterUserId || comment.is_own)
  }, [comments, filterUserId])

  const { preOp, postOp } = useMemo(() => splitCommentsByTag(scopedComments), [scopedComments])
  const nextTag = activeCommentTag(caseItem)
  const showPreOpPrimary = channel === 'review' && nextTag === 'pre_op'
  const canComposeReview = channel === 'review' && isUnderReview && (isConsultant || canStore)
  const canComposeNational =
    channel === 'national' && nationalCommentsEnabled && canStore && !caseItem.is_archived
  const canCompose = channel === 'national' ? canComposeNational : canComposeReview
  const allowAnonymous =
    channel === 'national' ? true : nextTag === 'post_op'

  const cardProps = {
    channel,
    editingCommentId,
    editingCommentBody,
    onEditingCommentBodyChange,
    onStartEdit,
    onCancelEdit,
    onSaveEdit,
    onDelete,
    canUpdate,
    canDelete,
    actionLoading,
  }

  if (channel === 'national') {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="font-display text-lg font-semibold">National Comments</h2>
          <p className="mt-1 text-sm text-ink-600">
            Discussion from doctors on this public National Hub case. Anonymous posts hide
            the uploader name from other doctors.
          </p>
        </div>

        {!nationalCommentsEnabled && (
          <p className="rounded-md border border-parch-100 bg-parch-50 px-3 py-2 text-sm text-ink-600">
            National Hub comments are not enabled for this case. The owner can allow them
            when the case is public (create/edit).
          </p>
        )}

        <ul className="space-y-3">
          {scopedComments.length === 0 ? (
            <li className="text-sm text-ink-500">No national comments yet.</li>
          ) : (
            scopedComments.map((comment) => (
              <CommentCard key={comment.id} comment={comment} {...cardProps} />
            ))
          )}
        </ul>

        {canCompose && (
          <div className="space-y-2 rounded-md border border-parch-100 p-4">
            <FieldLabel required>Add national comment</FieldLabel>
            <textarea
              className="min-h-24 w-full rounded-md border border-parch-200 px-3 py-2 text-sm"
              value={commentBody}
              onChange={(event) => onCommentBodyChange(event.target.value)}
              placeholder="Write a National Hub comment…"
            />
            {allowAnonymous && (
              <label className="flex items-start gap-2 text-sm text-ink-700">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={postAnonymously}
                  onChange={(event) => onPostAnonymouslyChange(event.target.checked)}
                />
                <span>
                  Post anonymously
                  <span className="mt-0.5 block text-xs text-ink-500">
                    Your name is hidden from other doctors. Super Admin retains identity in
                    the audit log.
                  </span>
                </span>
              </label>
            )}
            <Button
              size="sm"
              disabled={actionLoading || !commentBody.trim()}
              onClick={onAddComment}
            >
              Post
            </Button>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {!isUnderReview && (
        <p className="rounded-md border border-parch-100 bg-parch-50 px-3 py-2 text-sm text-ink-600">
          Feedback unlocks after this case is <strong>open for review</strong>. The consultant
          and assigned reviewers can then post on the shared thread.
        </p>
      )}

      {filterUserId ? (
        <p className="text-xs text-ink-500">
          Showing feedback from the selected reviewer. Clear the selection to see all review
          comments.
        </p>
      ) : null}

      {showPreOpPrimary ? (
        <section className="space-y-3">
          <h2 className="font-display text-lg font-semibold">Feedback</h2>
          <ul className="space-y-3">
            {preOp.length === 0 ? (
              <li className="text-sm text-ink-500">No feedback yet.</li>
            ) : (
              preOp.map((comment) => <CommentCard key={comment.id} comment={comment} {...cardProps} />)
            )}
          </ul>
        </section>
      ) : (
        <>
          <CollapsibleSection
            title="Feedback"
            comments={preOp}
            defaultOpen={preOp.length > 0 && postOp.length === 0}
          >
            {preOp.map((comment) => (
              <CommentCard key={comment.id} comment={comment} readOnly {...cardProps} />
            ))}
          </CollapsibleSection>

          <section className="space-y-3">
            <h3 className="font-display text-base font-semibold text-ink-900">
              Audit &amp; peer review (post-op)
            </h3>
            <p className="text-xs text-ink-500">
              Reviewers may post anonymously so feedback is judged on content, not seniority.
              Super Admin can still see author identity in the activity log.
            </p>
            <ul className="space-y-3">
              {postOp.length === 0 ? (
                <li className="text-sm text-ink-500">No post-op feedback yet.</li>
              ) : (
                postOp.map((comment) => (
                  <CommentCard key={comment.id} comment={comment} {...cardProps} />
                ))
              )}
            </ul>
          </section>
        </>
      )}

      {canCompose && !filterUserId && (
        <div className="space-y-2 rounded-md border border-parch-100 p-4">
          <FieldLabel required>Add Feedback</FieldLabel>
          <textarea
            className="min-h-24 w-full rounded-md border border-parch-200 px-3 py-2 text-sm"
            value={commentBody}
            onChange={(event) => onCommentBodyChange(event.target.value)}
            placeholder="Write your feedback…"
          />
          {allowAnonymous && (
            <label className="flex items-start gap-2 text-sm text-ink-700">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={postAnonymously}
                onChange={(event) => onPostAnonymouslyChange(event.target.checked)}
              />
              <span>
                Post anonymously
                <span className="mt-0.5 block text-xs text-ink-500">
                  Your name is hidden from the consultant and other reviewers. Super Admin
                  retains identity in the audit log.
                </span>
              </span>
            </label>
          )}
          <Button
            size="sm"
            disabled={actionLoading || !commentBody.trim()}
            onClick={onAddComment}
          >
            Post
          </Button>
        </div>
      )}

      {isUnderReview && !canCompose && (
        <p className="text-sm text-ink-500">
          Only the consultant and assigned case reviewers can comment while review is open.
        </p>
      )}
    </div>
  )
}
